import { round } from "lodash";
import { astStore } from "../store";
import { updateASTComponentItem, updateASTComponents } from "../store/common/action";
import {
  circleCompStylesEcho,
  frameCompStylesEcho,
  generateUUID,
  getPathBackgroundImage,
  groupCompStylesEcho,
  pathCompStylesEcho,
  rectCompStylesEcho,
  replaceEventOrHook,
  textCompStylesEcho,
} from '../utils';
import { ShapeConstants } from "../utils/penpot/constant";
import { calculateBound } from "../utils/penpot/util";
import { getRemValue, parseCssToTailwind } from "../utils/tailwindcss";
import { updateAtomsToDom } from './interactUtil.js';
import {canvasGlobal} from "./canvasGlobal";
import {convertInlineCss2Number} from '../utils';
import {DESIGNBOARD} from "../constant/common";


/**
 * 更新组件背景色
 * @param {*} compObj
 */
export const updateCompBackground = (compObj,{backgroundColor}) => {
  const defaultPathPrefix = 'dynamicCss.0.sources.0.css.normal';
  _set(compObj, `${defaultPathPrefix}.backgroundColor`, backgroundColor);
  updateASTComponentItem(compObj)
}

/**
 * 对象样式转成string
 * @param styleObj
 */
export const styleObjectToString = (styleObj) => {
  return Object.entries(styleObj)
    .filter(([key, value]) => key !== undefined && value !== undefined) // 过滤掉值为 undefined 的属性
    .map(([key, value]) => `${key}: ${value};`) // 转换为字符串
    .join(' '); // 拼接成最终字符串
};

/**
 * 绘画出SVG
 * @param css
 * @returns {SVGSVGElement}
 */
export const createSvgElementNS = (css) => {
  const { svgW, svgH, viewBox, imgX, imgY, patternW, patternH, pathSvg, pathStyles } = getPathCompParam(css);
  const { flag, href, pathContent } = getPathBackgroundImage(css);
  const FILL_IMAGE_RENDER = 'fill-image-render-1350-' + Math.random();
  const FILL_IMAGE_ID = 'url(#' + FILL_IMAGE_RENDER + ')';
  // 创建 SVG 命名空间
  const svgNS = 'http://www.w3.org/2000/svg';
  const xlinkNS = 'http://www.w3.org/1999/xlink';
  // 创建 SVG 元素
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', svgW + '');
  svg.setAttribute('height', svgH + '');
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('style', styleObjectToString(pathSvg));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('version', '1.1');
  svg.setAttribute('xmlns:xlink', xlinkNS); // 设置 xmlns:xlink

  // 创建 <defs> 和 <pattern> 元素
  if (flag) {
    const defs = document.createElementNS(svgNS, 'defs');
    const pattern = document.createElementNS(svgNS, 'pattern');
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('x', imgX);
    pattern.setAttribute('y', imgY);
    pattern.setAttribute('width', patternW);
    pattern.setAttribute('height', patternH);
    pattern.setAttribute('id', FILL_IMAGE_RENDER);

    const image = document.createElementNS(svgNS, 'image');
    image.setAttribute('href', href);
    image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    image.setAttribute('width', patternW);
    image.setAttribute('height', patternH);
    image.setAttribute('opacity', '1');

    pattern.appendChild(image);
    defs.appendChild(pattern);
    svg.appendChild(defs);
  }

  // 创建 <g> 和 <path> 元素
  const g = document.createElementNS(svgNS, 'g');
  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', pathContent.d);
  path.setAttribute('fill', FILL_IMAGE_ID);
  path.setAttribute('style', styleObjectToString(pathStyles));
  g.appendChild(path);
  svg.appendChild(g);
  return svg;
};


/**
 * 获取路径参数
 * @param css
 */
export const getPathCompParam = (css) => {
  let svgW = calculateBound(css, ShapeConstants.width);
  let svgH = calculateBound(css, ShapeConstants.height);
  let viewBox = `${css[ShapeConstants.x]} ${css[ShapeConstants.y]} ${calculateBound(css, ShapeConstants.width)} ${calculateBound(css, ShapeConstants.height)}`;
  let imgX = css[ShapeConstants.selrect][ShapeConstants.rect][ShapeConstants.x];
  let imgY = css[ShapeConstants.selrect][ShapeConstants.rect][ShapeConstants.y];
  let patternW = css[ShapeConstants.selrect][ShapeConstants.rect][ShapeConstants.width];
  let patternH = css[ShapeConstants.selrect][ShapeConstants.rect][ShapeConstants.height];
  let pathSvg = {
    '-webkit-print-color-adjust': 'exact',
    display: 'block',
  };
  let pathStyles = {
    fill: `${_get(css, [ShapeConstants.fills, 0, ShapeConstants.fillColor])}`,
    'fill-opacity': `${_get(css, [ShapeConstants.fills, 0, ShapeConstants.fillOpacity])}`,
    'stroke-width': `${_get(css, [ShapeConstants.strokes, 0, ShapeConstants.strokesWidth])}`,
    stroke: `${_get(css, [ShapeConstants.strokes, 0, ShapeConstants.strokesColor])}`,
    'stroke-opacity': `${_get(css, [ShapeConstants.strokes, 0, ShapeConstants.strokesOpacity])}`,
    'fill-rule': 'nonzero',
  };
  return {
    svgW, svgH, viewBox, imgX, imgY, patternW, patternH, pathSvg, pathStyles,
  };
};


/**
 * 设置树选中/取消选中样式
 * @param prop
 * @param choose
 * @param isHovered
 * @param isCanvas 是否在右边 Canvas 上操作
 */
export const changeTreeNodeStyle = async (prop, choose, isHovered = false, isCanvas = true) => {
  let tree = document.getElementById('listtree');

  // 仅在 Canvas 上点击时调用，展开对应节点
  if (isCanvas) {
    await customElements.whenDefined('tree-component');
    const treeHTML = document.getElementById('tree-component') as HTMLTreeComponentElement;
    await treeHTML.openSingleNode(prop);
  }

  let node = tree.querySelector(`[data-id="${prop}"]`);
  if (node) {
    choose ? node.classList.add('on') : node.classList.remove('on')
    isHovered ? node.classList.add('hovered') : node.classList.remove('hovered')
  }
}



/**
 * 删除子
 */
export const delTreeChild = (value, prop, index) => {
  const compObj = value.components[index];
  // 删除自己
  value.components.splice(index, 1);
  // 循环子
  for (let i = 0; i < compObj.children.length; i++) {
    const domProp = compObj.children[i].prop;
    const childrenIndex = findComponentsObj(value, domProp);
    delTreeChild(value, domProp, childrenIndex);
  }
  let canvases = document.getElementsByClassName('canvas'); // 获取所有 class 为 canvas 的元素
  for (let i = 0; i < canvases.length; i++) {
    let children = canvases[i].children; // 获取当前 canvas 元素的所有子元素
    for (let j = 0; j < children.length; j++) {
      if (children[j].id === prop) {
        children[j].parentNode.removeChild(children[j]);
        j--; // 因为删除了元素，所以需要将索引减1以避免跳过下一个元素
      }
    }
  }
};

/**
 * 查找数组对象下标
 */
export const findComponentsObj = (value, prop) => {
  return value.components.findIndex(item => item.prop === prop);
};

/**
 * 设置当前要复制组件的 prop, 方便后面粘贴使用
 * @param prop - .
 */
export function cloneComponents(prop: string) {

  canvasGlobal.prop = prop;
  // 2025-1-20，下面这一堆代码移植到粘贴函数 pasteComponents() 中
/*
  const components = _cloneDeep(astStore.state.components);
  const clones = [];

  function cloneComponent(prop: string, parent?: string) {
    const component = components.find(component => component.prop === prop);

    const newProp = generateUUID();

    const clone = {
      type: component.type,
      prop: newProp,
      parent: parent || component.parent,
      children: [],
      comps: _cloneDeep(component.comps),
      attrs: _cloneDeep(component.attrs),
      dynamicCss: _cloneDeep(component.dynamicCss),
      event: {},
      hook: {},
    };

    if (component.css) {
      _set(clone, 'css', _cloneDeep(component.css));
    }

    const newChildren = component.children.map(({ prop }) => ({ prop: cloneComponent(prop, newProp) }));

    _set(clone, 'children', newChildren);

    clones.push(clone);

    return newProp;
  }

  // 将首层复制的节点注册到其父节点的children中.
  const newProp = cloneComponent(prop);
  const component = components.find(component => component.prop === prop);
  const parentComponent = components.find(i => i.prop === component.parent);
  parentComponent.children.push({ prop: newProp });
  components.push(...clones);

  // 更新到AST.
  updateASTComponents(components);

  // 更新到DOM.
  updateAtomsToDom(clones, newProp);
  */
}


/**
 * 同层复制：复制当前原子组件及其后代.然后根据鼠标位置，右键粘贴到指定元素到指定位置，粘贴节点与复制节点同层，为兄弟节点
 */
export function pasteComponentsSameLevel() {
  let prop =  canvasGlobal.prop;

  if(prop == null || prop == ""){
    console.log(prop + "没有对象需要粘贴操作，请先复制！！！");
    return null;
  }

  const components = _cloneDeep(astStore.state.components);
  const clones = [];

  function cloneComponent(prop: string, xyFlag: boolean, parent?: string) {

    const component = components.find(component => component.prop === prop);

    const newProp = generateUUID(); // 设置新的 UUID

    const clone = {
      type: component.type,
      prop: newProp,
      parent: parent || component.parent,
      children: [],
      comps: _cloneDeep(component.comps),
      attrs: _cloneDeep(component.attrs),
      dynamicCss: _cloneDeep(component.dynamicCss),
      event: {},
      hook: {},
    };

    // 设置新的css样式
    if (component.css) {
      _set(clone, 'css', _cloneDeep(component.css));
    }

    // 只有复制父节点才需要根据鼠标的位置，重新设置组件的左上角位置
    if(xyFlag == true){
      // 获取父节点的面板的，根面板root的div的id是“pointcode-design-board”，其他面板的div的id是节点的 prop
      let parentDivId = (clone.parent == "root" ? DESIGNBOARD : clone.parent);
      const element = document.getElementById(parentDivId);

      // 获取根节点的面板的坐标
      const rect = element.getBoundingClientRect();

      // 左上角相对于视口（viewport）的坐标
      const left = rect.left;   // 相对于视口的 X 坐标
      const top = rect.top;     // 相对于视口的 Y 坐标

      console.log(`根面板 pointcode-design-board 的左上角坐标（相对于视口）: (${left}, ${top})`);

      let mouseX = canvasGlobal.mouseX;   // 获取坐标的 X 位置， 触发 组件右键事件 和 根面板右键事件 时，会自动刷新这个值
      let mouseY = canvasGlobal.mouseY;   // 获取坐标的 Y 位置， 触发 组件右键事件 和 根面板右键事件 时，会自动刷新这个值
      // 需要根据父节点的div左上角坐标，来重新计算粘贴的坐标
      clone.dynamicCss[0].sources[0].css.normal.left = "left-["+ ((mouseX - rect.left)/16) +"rem]";
      clone.dynamicCss[0].sources[0].css.normal.top = "top-[" + ((mouseY - rect.top)/16) + "rem]";

      console.log("粘贴数据显示 开始");
      console.log("X的坐标： " + mouseX);
      console.log("Y的坐标： " + mouseY);
      console.log("节点的css: ", clone);
      console.log("粘贴数据显示 结束");
    }

    // 获取子节点，并一层一层的递归初始化子节点的子节点，直至全部节点初始结束
    const newChildren = component.children.map(({ prop }) => ({ prop: cloneComponent(prop, false, newProp) }));

    _set(clone, 'children', newChildren);

    clones.push(clone);

    return newProp;
  }

  // 将首层复制的节点注册到其父节点的children中.
  const newProp = cloneComponent(prop, true);

  // 将克隆节点，挂到目标节点的同层下
  const component = components.find(component => component.prop === prop);
  const parentComponent = components.find(i => i.prop === component.parent);
  // 建立父子关系
  if (!parentComponent.children.some(child => child.prop === newProp)) {
    parentComponent.children.push({ prop: newProp });
  }

  components.push(...clones);

  // 更新到DOM.(updateAtomsToDom() 这个函数一定要在 updateASTComponents() 函数的前面，因为updateASTComponents()执行后，
  //           会自动触发recurseCalcCompsXY()的事件重新刷新界面，所以需要先执行updateAtomsToDom()创建dom)
  updateAtomsToDom(clones, newProp);

  // 更新到AST.
  updateASTComponents(components);

  // recurseCalcCompsXY(); // 需要重新刷新界面，布局计算，不采用清空设计区（画布）再回显 DOM 的方式，只重新计算布局，因设计区不是 canvas
}


/**
 * 指定粘贴：复制当前原子组件及其后代.然后根据鼠标位置，右键粘贴到指定元素到指定位置，粘贴节点会挂到距离鼠标附近最近的（面板，组别，循环区）父节点下面
 */
export function pasteComponentsDesignate() {
  let prop =  canvasGlobal.prop;
  let clickProp =  canvasGlobal.clickProp;
  if(prop == null || prop == ""){
    console.log(prop + "没有对象需要粘贴操作，请先复制！！！");
    return null;
  }

  const components = _cloneDeep(astStore.state.components);
  const clones = [];

  function cloneComponent(prop: string, xyFlag: boolean, parent?: string) {

    const component = components.find(component => component.prop === prop);

    const newProp = generateUUID(); // 设置新的 UUID

    const clone = {
      type: component.type,
      prop: newProp,
      parent: parent || component.parent,
      children: [],
      comps: _cloneDeep(component.comps),
      attrs: _cloneDeep(component.attrs),
      dynamicCss: _cloneDeep(component.dynamicCss),
      event: {},
      hook: {},
    };

    // 设置新的css样式
    if (component.css) {
      _set(clone, 'css', _cloneDeep(component.css));
    }

    // 只有复制父节点才需要根据鼠标的位置，重新设置组件的左上角位置
    if(xyFlag == true){
      // 获取父节点的面板的，根面板root的div的id是“pointcode-design-board”，其他面板的div的id是节点的 prop
      const element = document.getElementById(DESIGNBOARD);
      // 获取根节点的面板的坐标
      const rect = element.getBoundingClientRect();

      let mouseX = canvasGlobal.mouseX-rect.left;   // 获取坐标的 X 位置， 触发 组件右键事件 和 根面板右键事件 时，会自动刷新这个值(需要减去根面板的左上角坐标)
      let mouseY = canvasGlobal.mouseY-rect.top;   // 获取坐标的 Y 位置， 触发 组件右键事件 和 根面板右键事件 时，会自动刷新这个值(需要减去根面板的左上角坐标)

      const p_el = document.getElementById(clickProp === 'root' ? DESIGNBOARD : clickProp);
      const p_el_left = convertInlineCss2Number(p_el.style.left);
      const p_el_top = convertInlineCss2Number(p_el.style.top);
      const p_el_w = convertInlineCss2Number(p_el.style.width);
      const p_el_h = convertInlineCss2Number(p_el.style.height);
      // 范围
      let p_x = p_el_left + p_el_w;
      let p_y = p_el_top + p_el_h;

      let p_mincomp = {prop: clickProp, x: p_x, y: p_y, left: p_el_left, top: p_el_top, p_el: p_el}

      // 需要根据父节点的div左上角坐标，来重新计算粘贴的坐标
      clone.dynamicCss[0].sources[0].css.normal.left = "left-["+ ((mouseX - p_mincomp.left)/16) +"rem]";
      clone.dynamicCss[0].sources[0].css.normal.top = "top-[" + ((mouseY - p_mincomp.top)/16) + "rem]";

      // 获取新的父节点
      const p_comp = components.find(c => c.prop === p_mincomp.prop);

      // 建立父子关系
      clone.parent = p_mincomp.prop;
      if (!p_comp.children.some(child => child.prop === newProp)) {
        p_comp.children.push({prop: newProp});
      }

      console.log("粘贴数据显示 开始");
      console.log("X的坐标： " + mouseX);
      console.log("Y的坐标： " + mouseY);
      console.log("粘贴节点: ", clone);
      console.log("粘贴节点的新父节点: ", p_comp);
      console.log("粘贴数据显示 结束");
    }

    // 获取子节点，并一层一层的递归初始化子节点的子节点，直至全部节点初始结束
    const newChildren = component.children.map(({ prop }) => ({ prop: cloneComponent(prop, false, newProp) }));

    _set(clone, 'children', newChildren);

    clones.push(clone);

    return newProp;
  }

  // 将首层复制的节点注册到其父节点的children中.
  const newProp = cloneComponent(prop, true);

  // 将克隆节点，挂到目标节点的同层下
  const component = components.find(component => component.prop === prop);
  const parentComponent = components.find(i => i.prop === component.parent);
  // 建立父子关系
  if (!parentComponent.children.some(child => child.prop === newProp)) {
    parentComponent.children.push({ prop: newProp });
  }
  components.push(...clones); // 添加粘贴组件到总组件中


  // 更新到DOM.(updateAtomsToDom() 这个函数一定要在 updateASTComponents() 函数的前面，因为updateASTComponents()执行后，
  //           会自动触发recurseCalcCompsXY()的事件重新刷新界面，所以需要先执行updateAtomsToDom()创建dom)
  updateAtomsToDom(clones, newProp);

  // 更新到AST.
  updateASTComponents(components);

  // recurseCalcCompsXY(); // 需要重新刷新界面，布局计算，不采用清空设计区（画布）再回显 DOM 的方式，只重新计算布局，因设计区不是 canvas
}



/**
 * 删除
 * @param prop
 */
export const deleteComponents = async (prop) => {
  try {
    const confirmDelete = confirm('确认要删除元素吗？');
    if (confirmDelete) {
      const value =_cloneDeep(astStore.state);
      const index = findComponentsObj(value, prop);
      const index2 = findComponentsParentObj(value, prop);
      if (index > -1) {
        delTreeChild(value, prop, index);
        const tree = value['components'];
        // 删除无效的子节点
        cleanTree(tree);
        // 删除无效子节点之后输出修改后的树结构
       _set(value, 'components', tree);
      }
      // root 下是否存在
      const exists = value.components[0].children.some(item => item.prop === prop);
      if (exists) {
        if (index2 > -1)
          value.components[0].children.splice(index2, 1);
      }
      updateASTComponents(value.components);
    }
  } catch (e) {
    console.log('删除报错了', e);
  }
};

/**
 * 查找root下面的子
 * @param value
 * @param prop
 * @returns {*}
 */
export const findComponentsParentObj = (value, prop) => {
  return value.components[0].children.findIndex(item => item.prop === prop);
};


// 清理树，删除不存在的子节点
export function cleanTree(tree) {
  tree.forEach(parent => {
    // 过滤掉父节点中不存在的子节点
    parent.children = parent.children.filter(child => {
      const foundChild = findFrameByProp(tree, child.prop);
      return foundChild !== undefined;
    });
  });
}

// 查找对象
export function findFrameByProp(tree, prop) {
  return tree.find(frame => frame.prop === prop);
}



/**
 * 根据类型解析PP原子组件样式
 * @param compType
 * @param css
 * @returns {{boxShadow: string, backgroundAttachment?: string, backgroundColor?: string, backgroundImage?: string, display: string, backgroundClip?: string, filter: string, outline: string, transform: string, outlineStyle?: string, outlineOffset?: string, outlineWidth?: string, borderRadius: string, background?: string, outlineColor?: string, backgroundSize?: string, backgroundBlendMode?: string, backgroundPosition?: string, backgroundOrigin?: string, backgroundPositionX?: string, backgroundRepeat?: string, backgroundPositionY?: string}|{transform: string, display: string}|{webkitTextFillColor?: string, verticalAlign: VerticalAlignVal, fontStretch?: string, color?: string, textDecorationStyle?: string, textAlign: TextAlignVal, backgroundImage?: string, display: string, letterSpacing?: string, textDecorationLine?: string, fontVariant?: string, textDecoration?: string, textDecorationColor?: string, fontStyle?: string, textDecorationThickness?: string, backgroundClip?: string, filter: string, transform: string, fontFamily?: string, lineHeight?: string, fontSize?: string, fontWeight?: string | number, font?: string, textTransform?: string}|{backgroundAttachment?: string, backgroundColor?: string, d: string, backgroundImage?: string, display: string, backgroundClip?: string, transform: string, background?: string, backgroundSize?: string, backgroundBlendMode?: string, backgroundPosition?: string, backgroundOrigin?: string, backgroundPositionX?: string, backgroundRepeat?: string, backgroundPositionY?: string}}
 */
export const compTypeStyles = (compType, css) => {
  if (compType === 'text') {
    return textCompStylesEcho(css);
  } else if (compType === 'frame') {
    return frameCompStylesEcho(css);
  } else if (compType === 'rectangle') {
    return rectCompStylesEcho(css);
  } else if (compType === 'circle') {
    return circleCompStylesEcho(css);
  } else if (compType === 'group') {
    return groupCompStylesEcho(css);
  } else if (compType === 'path') {
    return pathCompStylesEcho(css);
  }
};



/**
 * 更新模板组件坐标 宽高 prop
 * @param dataList
 * @param objValue
 * @param uuid
 * @param width
 * @param height
 * @param left
 * @param top
 * @param hidden
 */
export const updateTemplate = (dataList, objValue, uuid, left, top,isChild = false) =>{
  const index = findChildProp(dataList, objValue.prop);
  if (index !== -1) {
    const defaultPathPrefix = 'dynamicCss.0.sources.0.css.normal';
    const rem = parseFloat(getRemValue());
    const format = value => `${round(value / rem, 4)}rem`;

    if (!isChild){  // 只有最外层的节点才需要重新设置左上角的位置
      // _set(dataList[index], `${defaultPathPrefix}.width`, parseCssToTailwind('width', format(width)));
      // _set(dataList[index], `${defaultPathPrefix}.height`, parseCssToTailwind('height', format(height)));
      _set(dataList[index], `${defaultPathPrefix}.left`, parseCssToTailwind('left', format(left)));
      _set(dataList[index], `${defaultPathPrefix}.top`, parseCssToTailwind('top', format(top)));
    }

    // 替换事件和钩子
    replaceEventOrHook(dataList, objValue.prop, uuid);
  }
}


/**
 * 查找child下标
 * @param value
 * @param prop
 * @returns {*}
 */
export const findChildProp = (value, prop) => {
  return value.findIndex(item => item.prop === prop);
};

